"use client"

import { useState, useRef, useEffect } from "react"
import { MessageList } from "./MessageList"
import { MessageInput } from "./MessageInput"
import { ProductCard } from "./ProductCard"
import { OrdersView } from "./OrdersView"
import { ServiceCard } from "./ServiceCard"
import { BookingsView } from "./BookingsView"
import { PaymentOptionsView } from "./PaymentOptionsView"
import { InvoiceView } from "./InvoiceView"
import { FeedbackView } from "./FeedbackView"
import { CartView } from "./CartView"
import type { Message, Product } from "../types"
import OnlineStatusBadge from "./OnlineStatus"

interface ChatWindowProps {
  isOpen: boolean
  onClose: () => void
  messages: Message[]
  isLoading: boolean
  error: string | null
  onSendMessage: (message: string) => void
  onClearMessages: () => void
  primaryColor?: string
  title?: string
  subtitle?: string
  placeholder?: string
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left"
  products?: Product[]
  onBuyProduct?: (product: Product) => void
  showProducts?: boolean
  onToggleProducts?: () => void
  voiceEnabled?: boolean
  apiKey?: string
  baseUrl?: string
  voiceConfig?: any
  orders?: any[]
  onToggleOrders?: () => void
  showOrders?: boolean
  onPayOrder?: (order: any) => void
  services?: any[]
  onBookService?: (service: any) => void
  showServices?: boolean
  onToggleServices?: () => void
  bookings?: any[]
  onToggleBookings?: () => void
  showBookings?: boolean
  bookingEnabled?: boolean
  onEndConversation?: () => void
  showPaymentOptions?: boolean
  onTogglePaymentOptions?: () => void
  showInvoice?: boolean
  onToggleInvoice?: () => void
  showFeedback?: boolean
  onToggleFeedback?: () => void
  onSubmitFeedback?: (feedback: { rating: number; comment: string }) => void
  showCart?: boolean
  onToggleCart?: () => void
  cartItems?: any[]
  onUpdateCartQuantity?: (id: string, quantity: number) => void
  onRemoveCartItem?: (id: string) => void
  onAddCartItem?: (productName: string) => void
  onCartCheckout?: () => void
}

export function ChatWindow({
  isOpen,
  onClose,
  messages,
  isLoading,
  error,
  onSendMessage,
  onClearMessages,
  primaryColor,
  title,
  subtitle,
  placeholder,
  position = "bottom-right",
  products = [],
  onBuyProduct,
  showProducts,
  onToggleProducts,
  voiceEnabled,
  apiKey,
  baseUrl,
  voiceConfig,
  orders = [],
  onToggleOrders,
  showOrders,
  onPayOrder,
  services = [],
  onBookService,
  showServices,
  onToggleServices,
  bookings = [],
  onToggleBookings,
  showBookings,
  bookingEnabled,
  onEndConversation,
  showPaymentOptions,
  onTogglePaymentOptions,
  showInvoice,
  onToggleInvoice,
  showFeedback,
  onToggleFeedback,
  onSubmitFeedback,
  showCart,
  onToggleCart,
  cartItems = [],
  onUpdateCartQuantity,
  onRemoveCartItem,
  onAddCartItem,
  onCartCheckout,
}: ChatWindowProps) {
  const [input, setInput] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    if (isOpen) {
      scrollToBottom()
    }
  }, [messages, isOpen])

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim())
      setInput("")
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        width: 'min(380px, calc(100vw - 20px))',
        height: 'min(600px, calc(100vh - 100px))',
        maxWidth: '380px',
        maxHeight: '600px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9998,
        border: '1px solid #e5e7eb',
        bottom: position.includes('bottom') ? '90px' : 'auto',
        top: position.includes('top') ? '90px' : 'auto',
        right: position.includes('right') ? '20px' : 'auto',
        left: position.includes('left') ? '20px' : 'auto'
      }}
      className="chat-window-responsive"
    >
      {/* Header with integrated chat bubble */}
      <div style={{
        background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
        color: 'white',
        padding: 'min(16px, 12px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        position: 'relative'
      }}>
        <div>
          <h3 style={{ fontWeight: '600', fontSize: '16px', margin: 0 }}>{title}</h3>
          <OnlineStatusBadge status="online" />
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {products.length > 0 && onToggleProducts && (
            <button onClick={onToggleProducts} style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </button>
          )}
          {onToggleCart && (
            <button onClick={onToggleCart} style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 7M7 13l-2.8 8.4a2 2 0 002 2.6h7.6" />
              </svg>
              {cartItems.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>
          )}
          {products?.length > 0 && onToggleOrders && (
            <button onClick={onToggleOrders} style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 7M7 13l-2 9m5-9v9m6-9v9m-6 0h6" />
              </svg>
              {/* <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg> */}
            </button>
          )}
          {bookingEnabled && services.length > 0 && onToggleServices && (
            <button onClick={onToggleServices} style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {/* <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0l-.5 8.5A2 2 0 0013.5 21h-3A2 2 0 018.5 15.5L8 7z" />
              </svg> */}
            </button>
          )}
          {bookingEnabled && services.length > 0 && onToggleBookings && (
            <button onClick={onToggleBookings} style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {/* <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0l-.5 8.5A2 2 0 0013.5 21h-3A2 2 0 018.5 15.5L8 7z" />
              </svg> */}
            </button>
          )}

          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
          }} onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}>
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#f9fafb'
      }}>
        {messages.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'var(--background-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="36" height="36" fill="none" stroke="var(--text-muted)" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h4 style={{ fontSize: '20px', marginBottom: '12px', fontWeight: '600' }}>{title}</h4>
            <p style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>{subtitle}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Type a message to get started</p>
          </div>
        ) : showOrders ? (
          <OrdersView
            orders={orders}
            onClose={() => onToggleOrders && onToggleOrders()}
            onPayOrder={onPayOrder}
          />
        ) : showProducts ? (
          <div>
            <h4 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600' }}>Products</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '12px',
              margin: '16px 0'
            }}>
              {products.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onBuyNow={onBuyProduct!}
                  primaryColor={primaryColor as string}
                />
              ))}
            </div>
          </div>
        ) : showServices ? (
          <div>
            <h4 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600' }}>Services</h4>
            {services.map((service) => (
              <ServiceCard
                key={service._id}
                service={service}
                onBook={onBookService!}
              />
            ))}
          </div>
        ) : showBookings ? (
          <BookingsView
            bookings={bookings}
            onClose={() => onToggleBookings && onToggleBookings()}
          />
        ) : showPaymentOptions ? (
          <PaymentOptionsView
            onClose={() => onTogglePaymentOptions && onTogglePaymentOptions()}
            primaryColor={primaryColor}
          />
        ) : showInvoice ? (
          <InvoiceView
            onClose={() => onToggleInvoice && onToggleInvoice()}
            primaryColor={primaryColor}
          />
        ) : showFeedback ? (
          <FeedbackView
            onClose={() => onToggleFeedback && onToggleFeedback()}
            onSubmit={onSubmitFeedback!}
            primaryColor={primaryColor}
          />
        ) : (
          <MessageList
            messages={messages}
            primaryColor={primaryColor as string}
            isLoading={isLoading}
            onEndConversation={onEndConversation}
            onShowProducts={onToggleProducts}
            onShowServices={onToggleServices}
            onShowBookingForm={() => {}}
            onShowPaymentOptions={onTogglePaymentOptions}
            onShowInvoice={onToggleInvoice}
            onRequestFeedback={onToggleFeedback}
            onShowCart={onToggleCart}
          />
        )}

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '14px',
            margin: '12px 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p style={{ margin: 0 }}>{error}</p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        value={input}
        onChange={setInput}
        onSend={handleSend}
        isLoading={isLoading}
        placeholder={placeholder as string}
        primaryColor={primaryColor as string}
        voiceEnabled={voiceEnabled}
        apiKey={apiKey}
        baseUrl={baseUrl}
        voiceConfig={voiceConfig}
      />

      {/* Cart Sheet */}
      <CartView
        isOpen={showCart || false}
        onClose={() => onToggleCart && onToggleCart()}
        items={cartItems}
        onUpdateQuantity={onUpdateCartQuantity!}
        onRemoveItem={onRemoveCartItem!}
        onAddItem={onAddCartItem!}
        onCheckout={onCartCheckout!}
        primaryColor={primaryColor}
        currency={products[0]?.currency || 'GHS'}
      />
    </div>
  )
}